<script setup lang="ts">
/**
 * CampaignMobileActions — barre de boutons mobile (Je participe / Partager),
 * avec états "clôturée" / "en pause".
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps.
 *
 * Le composant ne connaît PAS la destination du don : il émet `donate` et
 * chaque page décide (public → naviguer, preview → simuler).
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignMobileActions from "@donoor/ui/campaign/CampaignMobileActions.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { Campaign } from "./types";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`
// et `commun.*` (bouton Partager), présentes dans donoor.org ET dashboard.
const { t } = useI18n();

defineProps<{
  campaign: Campaign;
  isCampaignClosed: boolean;
  isCampaignPaused: boolean;
}>();

const emit = defineEmits<{
  openShare: [];
  donate: [];
}>();
</script>

<template>
  <div v-if="isCampaignClosed" class="py-3 px-4 rounded-full bg-gray-100 flex items-center justify-center gap-2">
    <UIcon name="i-lucide-lock" class="size-4 text-gray-500" />
    <span class="text-sm font-bold text-gray-600 uppercase tracking-wide">
      {{ t("ui.commun.cagnotteCloturee") }}
    </span>
  </div>

  <div v-else-if="isCampaignPaused" class="py-3 px-4 rounded-full bg-amber-50 flex items-center justify-center gap-2">
    <UIcon name="i-lucide-pause-circle" class="size-4 text-amber-600" />
    <span class="text-sm font-bold text-amber-700 uppercase tracking-wide">
      {{ t("ui.commun.cagnotteEnPause") }}
    </span>
  </div>

  <div v-else class="grid grid-cols-2 gap-3 mb-8">
    <button
      type="button"
      class="py-3 rounded-full text-sm font-bold bg-[#0F3D8C] hover:bg-[#0A2D6B] active:scale-[0.98] text-white transition-all shadow-sm"
      @click="emit('donate')"
    >
      {{ t("ui.commun.jeParticipe") }}
    </button>
    <button
      type="button"
      class="py-3 rounded-full text-sm font-bold bg-[#D7EBFF] hover:bg-[#A8D2FE] active:scale-[0.98] text-[#0F3D8C] transition-all"
      @click="emit('openShare')"
    >
      {{ t("commun.actions.partager") }}
    </button>
  </div>
</template>
